import {
  ATOMIC_SUM_TARGET,
  countOf,
  DIGIT_SUM_TARGET,
  digitSum,
  hasAtomicPairSumming,
  hasPalindrome,
  isPrime,
  MONTHS,
  PET,
  ROMAN_PRODUCT_TARGET,
  ROMAN_VALUES,
  romanProduct,
  SPONSORS,
  WORM,
  WORMS_REQUIRED
} from './passwordData';

/**
 * Everything a rule is allowed to look at. Dynamic values (the CAPTCHA code,
 * the sacrificed letters, today's date and moon) are passed in rather than read
 * from the clock so the rules stay pure, testable and SSR-safe.
 */
export type PasswordContext = {
  password: string;
  captcha: string;
  sacrificed: string[];
  moonEmoji: string;
  dayOfMonth: number;
};

export type PasswordRule = {
  id: number;
  label: (ctx: PasswordContext) => string;
  test: (ctx: PasswordContext) => boolean;
  /**
   * Rules that need the game to do something first (hand out a CAPTCHA, hatch
   * the chick, start a fire). The reducer keys its side effects off these.
   */
  trigger?: 'captcha' | 'sacrifice' | 'pet' | 'worms' | 'fire';
};

/**
 * Ordered as they unlock: rule n+1 appears only once rules 1..n all pass.
 *
 * Two hard constraints when editing this list:
 *  - Every rule must be satisfiable *simultaneously* with all the others. The
 *    suite proves this against a known-good password, so run it.
 *  - Nothing here may force digits except where accounted for. The digit-sum
 *    rule is deliberately late and generous (100, reachable by padding upward)
 *    because a tight target plus forced digits elsewhere is unwinnable.
 */
export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 1,
    label: () => 'Your password must be at least 8 characters long.',
    test: ({ password }) => password.length >= 8
  },
  {
    id: 2,
    label: () => 'Your password must include a number.',
    test: ({ password }) => /\d/.test(password)
  },
  {
    id: 3,
    label: () => 'Your password must include an uppercase letter.',
    test: ({ password }) => /[A-Z]/.test(password)
  },
  {
    id: 4,
    label: () => 'Your password must include a special character.',
    test: ({ password }) => /[^\p{L}\p{N}\s]/u.test(password)
  },
  {
    id: 5,
    label: () => 'Your password must include a month of the year.',
    test: ({ password }) => {
      const lower = password.toLowerCase();
      return MONTHS.some((month) => lower.includes(month));
    }
  },
  {
    id: 6,
    label: () => 'Your password must include a Roman numeral.',
    test: ({ password }) => [...password].some((char) => char in ROMAN_VALUES)
  },
  {
    id: 7,
    label: () =>
      'Your password must include one of our sponsors: factiii, tron or truth.',
    test: ({ password }) => {
      const lower = password.toLowerCase();
      return SPONSORS.some((sponsor) => lower.includes(sponsor));
    }
  },
  {
    id: 8,
    label: () =>
      `The Roman numerals in your password must multiply to exactly ${ROMAN_PRODUCT_TARGET}.`,
    test: ({ password }) => romanProduct(password) === ROMAN_PRODUCT_TARGET
  },
  {
    id: 9,
    label: ({ dayOfMonth }) =>
      `Your password must include today's day of the month (${dayOfMonth}).`,
    test: ({ password, dayOfMonth }) => password.includes(String(dayOfMonth))
  },
  {
    id: 10,
    label: () =>
      'Your password must contain a palindrome at least 3 characters long.',
    test: ({ password }) => hasPalindrome(password)
  },
  {
    id: 11,
    label: () => 'Your password must include the answer to 6 × 7.',
    test: ({ password }) => password.includes('42')
  },
  {
    id: 12,
    label: () => 'Your password must include the word "fiction", backwards.',
    test: ({ password }) => password.toLowerCase().includes('noitcif')
  },
  {
    id: 13,
    label: () =>
      'Your password must include a hex colour code, like #a4c9f0 or #beeeef.',
    test: ({ password }) => /#[0-9a-f]{6}/i.test(password)
  },
  {
    id: 14,
    label: () => 'Your password must include a chess move, like e4 or Nf3.',
    test: ({ password }) => /(?:[KQRBN])?[a-h][1-8]/.test(password)
  },
  {
    id: 15,
    label: () =>
      `Your password must include two chemical symbols whose atomic numbers add up to ${ATOMIC_SUM_TARGET}. (Ca is 20.)`,
    test: ({ password }) => hasAtomicPairSumming(password, ATOMIC_SUM_TARGET)
  },
  {
    id: 16,
    label: ({ moonEmoji }) =>
      `Your password must include tonight's moon phase: ${moonEmoji}`,
    test: ({ password, moonEmoji }) => password.includes(moonEmoji)
  },
  {
    id: 17,
    trigger: 'captcha',
    label: ({ captcha }) =>
      `Your password must include our sponsor's CAPTCHA: ${captcha}`,
    test: ({ password, captcha }) =>
      captcha.length > 0 && password.includes(captcha)
  },
  {
    id: 18,
    trigger: 'sacrifice',
    label: ({ sacrificed }) =>
      sacrificed.length === 0
        ? 'Sacrifice two letters. You may never use them again.'
        : `You sacrificed ${sacrificed
            .map((letter) => letter.toUpperCase())
            .join(' and ')}. They may never appear again.`,
    test: ({ password, sacrificed }) => {
      if (sacrificed.length === 0) return false;
      const lower = password.toLowerCase();
      return sacrificed.every((letter) => !lower.includes(letter));
    }
  },
  {
    id: 19,
    trigger: 'pet',
    label: () =>
      `${PET} A chick has hatched in your password. Keep it in there — deleting it is fatal.`,
    test: ({ password }) => password.includes(PET)
  },
  {
    id: 20,
    trigger: 'worms',
    label: () =>
      `${PET} The chick is hungry. Your password must contain ${WORMS_REQUIRED} worms (${WORM}).`,
    test: ({ password }) => countOf(password, WORM) >= WORMS_REQUIRED
  },
  {
    id: 21,
    trigger: 'fire',
    label: () =>
      '🔥 Your password is on fire. Burning characters will be destroyed — get rid of them.',
    test: ({ password }) => !password.includes('🔥')
    // The fire itself lives in the reducer; the rule just insists you put it out.
  },
  {
    id: 22,
    label: () => 'The length of your password must be a prime number.',
    test: ({ password }) => isPrime([...password].length)
  },
  {
    id: 23,
    label: () =>
      `The digits in your password must add up to exactly ${DIGIT_SUM_TARGET}.`,
    test: ({ password }) => digitSum(password) === DIGIT_SUM_TARGET
  },
  {
    id: 24,
    label: () =>
      'Finally: your password must not contain the word "password". Obviously.',
    test: ({ password }) => !password.toLowerCase().includes('password')
  }
];

export const countPassing = (ctx: PasswordContext) =>
  PASSWORD_RULES.filter((rule) => rule.test(ctx)).length;

/**
 * How many rules should be on screen, never fewer than already unlocked —
 * rules do not go back into hiding once seen.
 */
export const nextRevealed = (ctx: PasswordContext, revealed: number) => {
  let count = revealed;

  while (
    count < PASSWORD_RULES.length &&
    PASSWORD_RULES.slice(0, count).every((rule) => rule.test(ctx))
  ) {
    count += 1;
  }

  return count;
};

/** Triggers that should be armed for the currently revealed rules. */
export const triggersFor = (revealed: number) =>
  PASSWORD_RULES.slice(0, revealed)
    .map((rule) => rule.trigger)
    .filter((trigger): trigger is NonNullable<PasswordRule['trigger']> =>
      Boolean(trigger)
    );
